import { createContext, useContext, type Dispatch, type SetStateAction } from 'react';

/**
 * Whether the nearest ancestor `<SideNav>` is currently collapsed to a rail. Read via
 * `useSideNavCollapsed()` by `SideNavItem`/`SideNavGroup` so they can hide labels and switch
 * to icon-only rendering without every consumer wiring that logic by hand.
 */
export const SideNavContext = createContext(false);

export function useSideNavCollapsed(): boolean {
  return useContext(SideNavContext);
}

export interface SideNavFlyoutPinningContextValue {
  /** `SideNav`'s own `pinFlyoutOnClick` prop. */
  pinOnClick: boolean;
  /** `useId()` of whichever `SideNavFlyout` a click most recently pinned open, if any. */
  pinnedId: string | null;
  // Dispatch (not a plain setter) so a flyout clearing *its own* pin can use the functional
  // updater form (`setPinnedId(prev => (prev === id ? null : prev))`) - clicking a different
  // trigger updates this state before the just-closed flyout's click-away handler runs (its
  // own component hasn't re-rendered yet, so its "am I still pinned" check is stale), and a
  // plain `setPinnedId(null)` there would clobber the new pin that was just set moments
  // earlier in the same event.
  setPinnedId: Dispatch<SetStateAction<string | null>>;
  /**
   * `useId()` of whichever unpinned `SideNavFlyout` is currently open (hover or click), if
   * any. Lets a newly-opened flyout force-close a sibling immediately instead of waiting out
   * its `closeDelayMs` grace period - without this, moving the pointer directly from one
   * trigger to an adjacent one (e.g. the footer's account menu and app switcher rows, only a
   * few pixels apart) leaves both flyouts visible at once for that whole delay, which reads
   * as the popup content jumping/overlapping as the stale one closes late.
   */
  activeId: string | null;
  /** Dispatch for the same "clear only if it's still mine" reason as `setPinnedId`. */
  setActiveId: Dispatch<SetStateAction<string | null>>;
}

/**
 * Lets every `SideNavFlyout` under one `<SideNav>` share a single "which one is pinned"
 * slot, so pinning one (via a click, when `pinFlyoutOnClick` is set) closes whichever other
 * one was previously pinned - only one flyout should stay forced open at a time. Also shares
 * "which one is currently open at all" so a newly-opened flyout can close a sibling straight
 * away rather than racing its close-delay timer. Undefined outside a `SideNav` (or when a
 * `SideNavFlyout` is used standalone), in which case pinning and cross-flyout exclusivity are
 * simply disabled and every flyout falls back to its plain hover/click-to-open behaviour.
 */
export const SideNavFlyoutPinningContext = createContext<SideNavFlyoutPinningContextValue | undefined>(undefined);
